<script setup lang="ts">
import { defineProps } from 'vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import Button from '@/dashboard/vue/components/common/Button.vue';
import { POPUPS_ANIMATION } from '@/dashboard/constants/popups';

const props = defineProps<{
  show: boolean;
  title: string;
  subtitle: string;
  okTitle?: string;
  cancelTitle?: string;
  ok: () => void;
  cancel?: () => void;
}>();
</script>

<template>
  <TransitionRoot :show="props.show" as="template">
    <Dialog class="fixed bottom-zero left-zero right-zero top-zero z-[5000]">
      <!-- The backdrop, rendered as a fixed sibling to the panel container -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.background.enter"
        :enter-from="POPUPS_ANIMATION.background.enterFrom"
        :enter-to="POPUPS_ANIMATION.background.enterTo"
        :leave="POPUPS_ANIMATION.background.leave"
        :leave-from="POPUPS_ANIMATION.background.leaveFrom"
        :leave-to="POPUPS_ANIMATION.background.leaveTo"
      >
        <div class="inset-0 fixed bottom-zero left-zero right-zero top-zero h-full w-full bg-gray-250/60 dark:bg-black/60" aria-hidden="true" />
      </TransitionChild>

      <!-- Full-screen container to center the panel -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.content.enter"
        :enter-from="POPUPS_ANIMATION.content.enterFrom"
        :enter-to="POPUPS_ANIMATION.content.enterTo"
        :leave="POPUPS_ANIMATION.content.leave"
        :leave-from="POPUPS_ANIMATION.content.leaveFrom"
        :leave-to="POPUPS_ANIMATION.content.leaveTo"
      >
        <div class="absolute h-full w-full overflow-auto p-[47px]">
          <div class="flex min-h-full flex-1 items-center justify-center">
            <!-- The actual dialog panel -->
            <DialogPanel class="w-[520px] rounded-16 bg-white px-s pb-s pt-xl text-center dark:bg-gray-900 max-tablet:px-s">
              <h5 class="mb-s text-black dark:text-gray-400">{{ props.title }}</h5>
              <p class="text-content-m mb-l px-l text-gray-900 dark:text-gray-500">
                {{ props.subtitle }}
              </p>

              <div class="flex justify-between gap-xs">
                <Button v-if="props.cancel" v-focus class="button-ghost h-[40px] max-w-[138px]" :on-click="props.cancel">
                  <span class="button__text">{{ cancelTitle ?? 'No' }}</span>
                </Button>

                <Button class="button-colored h-[40px] max-w-[138px]" :on-click="props.ok">
                  <span class="button__text">{{ okTitle ?? 'Yes' }}</span>
                </Button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>
